import { useCallback } from 'react';
import {
  type GDSIconSource,
  type GDSIconToken,
  type GDSIcons,
} from '../../theme/icons/get-theme-icons';
import { useThemeIcons } from '../../hooks/use-theme';

/**
 * This hook provides an API get the current theme's icon map, with additional utilities to validate GDS icon tokens and paths therein
 * @returns {ReturnType<typeof useGDSIcon>} *{ iconMap, isGDSIconToken, getGDSIconPath }*
 */
export const useGDSIcon = () => {
  const iconMap: GDSIcons = useThemeIcons();

  const getGDSIconPath = useCallback(
    (iconToken: GDSIconSource) => {
      const resolvedIcon = iconMap?.[iconToken];

      // If an icon path exists for the token provided, append the  `iconToken` as the resolvedIcon's `#id`.
      if (typeof resolvedIcon === 'string')
        return resolvedIcon.concat('#', iconToken);

      // Otherwise it may be undefined because it uses the old `illustrative` verbiage, attempt to replace it with `descriptive`
      if (iconToken?.includes?.('illustrative')) {
        const descriptiveToken = iconToken.replace(
          'illustrative',
          'descriptive',
        );

        // eslint-disable-next-line no-console
        console.warn(
          `GDS Deprecation Error: use ${descriptiveToken} instead of ${iconToken}`,
        );

        return iconMap?.[descriptiveToken]?.concat?.('#', descriptiveToken);
      }

      return resolvedIcon;
    },
    [iconMap],
  );

  const isGDSIconToken = useCallback(
    (iconToken: any | GDSIconSource): iconToken is GDSIconToken =>
      getGDSIconPath(iconToken) != null,
    [getGDSIconPath],
  );

  return { iconMap, isGDSIconToken, getGDSIconPath };
};
